// Recently viewed entities, tracked client-side in localStorage.
//
// Each workspace key gets its own slot so switching workspaces doesn't
// surface unrelated entities. Entries are deduped by (entity_type, id);
// re-visiting an entity bumps it to the front. Capped at MAX_ENTRIES.

import type { AttachableEntity } from "./types";

const STORAGE_KEY_PREFIX = "scaicrm.recent.";
const MAX_ENTRIES = 25;

export type RecentView = {
  entity_type: AttachableEntity;
  id: string;
  name: string;
  viewed_at: string; // ISO timestamp
  // Optional subtitle (lifecycle stage, primary domain, status, etc.) so the
  // home widget can render context without re-fetching.
  subtitle?: string | null;
};

function storageKey(wsKey: string): string {
  return `${STORAGE_KEY_PREFIX}${wsKey}`;
}

export function recordView(wsKey: string, view: Omit<RecentView, "viewed_at">): void {
  if (!wsKey || !view.id) return;
  try {
    const raw = localStorage.getItem(storageKey(wsKey));
    const list: RecentView[] = raw ? JSON.parse(raw) : [];
    // Drop any prior entry for the same (type, id) so the new one bumps to front.
    const filtered = list.filter(
      (v) => !(v.entity_type === view.entity_type && v.id === view.id),
    );
    filtered.unshift({ ...view, viewed_at: new Date().toISOString() });
    localStorage.setItem(
      storageKey(wsKey),
      JSON.stringify(filtered.slice(0, MAX_ENTRIES)),
    );
  } catch {
    // localStorage can throw under private-browsing / quota — fail silently;
    // recently-viewed is a nicety, not a feature dependency.
  }
}

export function readRecentViews(wsKey: string, limit = 10): RecentView[] {
  if (!wsKey) return [];
  try {
    const raw = localStorage.getItem(storageKey(wsKey));
    if (!raw) return [];
    const list: RecentView[] = JSON.parse(raw);
    return Array.isArray(list) ? list.slice(0, limit) : [];
  } catch {
    return [];
  }
}

export function clearRecentViews(wsKey: string): void {
  try {
    localStorage.removeItem(storageKey(wsKey));
  } catch {
    /* see above */
  }
}
